import { Box, Button, Divider, Grid, Stack, Typography } from "@mui/material";
import { OrganizationUserContext } from "contexts/Organization";
import { SubscriptionContext } from "contexts/Subscription";
import { useContext } from "react";
import { Plans } from "schemas/payments";

import { useSubscription } from "utils/useSubscription";

import PlansPrice from "../SharedComponents/Price";
import VATerms from "../SharedComponents/VATerms";
import { PlanAction, getCurrentPlanButton } from "../helpers";

const FEATURES = [
  {
    icon: "fa-solid fa-bell",
    title: "Smart Notifications",
    description:
      "Email alerts when brands need review or automations need attention",
    iconBg: "#b1b3f8",
    borderColor: "rgba(177,179,248,0.3)",
    background:
      "linear-gradient(150deg, rgba(177, 179, 248, 0.1) 0%, rgba(177, 179, 248, 0.2) 100%)",
  },
  {
    icon: "fa-solid fa-clock",
    title: "Smart Timings",
    description:
      "Auto-send during business hours, avoid weekends for max engagement",
    iconBg: "#f7ee6f",
    borderColor: "rgba(247,238,111,0.4)",
    background:
      "linear-gradient(150deg, rgba(247, 238, 111, 0.1) 0%, rgba(247, 238, 111, 0.2) 100%)",
  },
  {
    icon: "fa-solid fa-bullseye",
    title: "Brand Alignment",
    description:
      "Location alignment & exclude misaligned categories automatically",
    iconBg: "#00a63e",
    iconColor: "white",
    borderColor: "#b9f8cf",
    background:
      "linear-gradient(150deg, rgb(240, 253, 244) 0%, rgba(220, 252, 231, 0.5) 100%)",
  },
];

const MobilePlanCardAutopilot = () => {
  const { onPlanButtonClick } = useSubscription();
  const { currentOrg } = useContext(OrganizationUserContext);
  const { plans, subscription } = useContext(SubscriptionContext);
  const action = getCurrentPlanButton(
    Plans.AUTOPILOT,
    subscription?.subscription?.planName || Plans.FREE,
    subscription,
  );
  const totalEmails = plans?.[Plans.AUTOPILOT]?.monthly_limit || 0;

  return (
    <Box sx={{ backgroundColor: "white", borderRadius: 3, mt: 1 }}>
      <Grid container justifyContent={"flex-end"} sx={{ pr: 1, pt: 1 }}>
        <Box
          sx={{
            backgroundColor: "#f59e0b",
            color: "white",
            borderRadius: 6,
            px: 2,
            py: 0.5,
            fontSize: 11,
            mb: 2,
            fontWeight: 600,
          }}
        >
          <Box component="span" className="fa-solid fa-crown" /> Hands-off
          pitching for busy creators
        </Box>
      </Grid>

      <Box sx={{ px: 3, pb: 3 }}>
        <Grid container justifyContent="space-between" alignItems="flex-end">
          <Typography variant="h4" sx={{ fontSize: 26 }}>
            Autopilot
          </Typography>
          <PlansPrice plan={Plans.AUTOPILOT} />
        </Grid>

        <Divider sx={{ my: 1, backgroundColor: "black" }} />

        <Typography>
          <strong>Includes everything in Pro, plus:</strong>
        </Typography>

        <Typography sx={{ fontSize: 14, lineHeight: 2 }}>
          🚀 Send {totalEmails} emails per month
          <br />
          🤖 Input your preferences for smart sourcing brands from sources like
          Instagram, TikTok, Google Maps & more
          <br />
          ✍️ Runs on autopilot so you set it up once and have weekly pitches
          sent automatically
          <br />
          🎯 Full control to review, approve, or pause any time
          <br />
          💸 Exclusive brand pay data
          <br />
        </Typography>

        <Stack spacing={1} sx={{ mt: 2, mb: 1 }}>
          {FEATURES.map((feature, index) => (
            <Box
              key={index}
              sx={{
                p: 2,
                borderRadius: "14px",
                border: `1px solid ${feature.borderColor}`,
                background: feature.background,
              }}
            >
              <Grid container spacing={1.5} alignItems="flex-start">
                <Grid item xs="auto">
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: "10px",
                      backgroundColor: feature.iconBg,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Box
                      component="span"
                      className={feature.icon}
                      sx={{
                        fontSize: 16,
                        color: feature.iconColor || "#014339",
                      }}
                    />
                  </Box>
                </Grid>
                <Grid item xs>
                  <Typography
                    sx={{
                      fontWeight: 700,
                      fontSize: 14,
                      color: "#014339",
                      lineHeight: "20px",
                    }}
                  >
                    {feature.title}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: 12,
                      color: "rgba(1,67,57,0.7)",
                      lineHeight: "19.5px",
                    }}
                  >
                    {feature.description}
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          ))}
        </Stack>

        {!currentOrg?.overridePlanName && (
          <Button
            disableElevation
            fullWidth
            variant={action === PlanAction.upgrade ? "contained" : "text"}
            sx={{
              fontSize: 16,
              mt: 2,
              py: 1,
              borderRadius: 2,
              backgroundColor:
                action === PlanAction.upgrade ? "#4338ca" : "#f4f8f6",
              color: action === PlanAction.upgrade ? "white" : undefined,
              "&:hover": {
                backgroundColor:
                  action === PlanAction.upgrade ? "#3730a3" : "#e8ece9",
              },
            }}
            onClick={() => onPlanButtonClick(Plans.AUTOPILOT, action)}
          >
            Upgrade to Autopilot
          </Button>
        )}

        <Grid container justifyContent={"center"} sx={{ mt: 1 }}>
          <VATerms plan={Plans.AUTOPILOT} />
        </Grid>
      </Box>
    </Box>
  );
};

export default MobilePlanCardAutopilot;
